import { Fragment, h } from 'preact'
import { useEffect } from 'preact/hooks'

import Modal from 'components/Modal'
import Icon from 'components/Icon'
import Button from 'components/Button'

import './index.sass'

export default function SupportSuccessModal({ organization, amount, onClose, isRecurring }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, 15000)
    return () => clearTimeout(timer)
  }, [onClose])

  const getMessageTier = () => {
    const dollars = amount / 100
    if (isRecurring) {
      if (dollars >= 100) {
        return 'recurring-high'
      }
      return 'recurring-low'
    } else if (dollars >= 100) {
      return 'onetime-high'
    } else {
      return 'onetime-low'
    }
  }

  const messages = {
    'recurring-high': {
      title: 'Incredible Generosity! 🌟',
      primary: "You're making an extraordinary impact!",
      secondary: (
        <Fragment>
          Your monthly contribution of
          <strong className="SupportSuccessModal-amount"> ${(amount / 100).toFixed(2)}</strong>
          {' '}to <strong>{organization.name}</strong> is truly transformative!
        </Fragment>
      ),
      impact: (
        <Fragment>
          <Icon type="star" className="SupportSuccessModal-impact-icon" />
          <p>
            As a major monthly supporter, you're not just supporting—you're building the future of this community!
            Your sustained commitment enables long-term planning, ambitious projects, and lasting change.
            The entire community thanks you! 🙏
          </p>
        </Fragment>
      )
    },
    'recurring-low': {
      title: 'Thank You for Your Commitment! 💙',
      primary: "You're part of something special!",
      secondary: (
        <Fragment>
          Your monthly support of <strong className="SupportSuccessModal-amount">${(amount / 100).toFixed(2)}</strong>
          {' '}to <strong>{organization.name}</strong> makes a real difference!
        </Fragment>
      ),
      impact: (
        <Fragment>
          <Icon type="heart" className="SupportSuccessModal-impact-icon" />
          <p>
            Monthly supporters like you provide the steady foundation that allows communities to thrive.
            Your ongoing commitment means the world to us and enables consistent growth and improvement.
            Every month, you're making an impact!
          </p>
        </Fragment>
      )
    },
    'onetime-high': {
      title: 'Wow! Thank You! 🎉',
      primary: "What an amazing contribution!",
      secondary: (
        <Fragment>
          Your generous gift of <strong className="SupportSuccessModal-amount">${(amount / 100).toFixed(2)}</strong>
          {' '}to <strong>{organization.name}</strong> is deeply appreciated!
        </Fragment>
      ),
      impact: (
        <Fragment>
          <Icon type="gift" className="SupportSuccessModal-impact-icon" />
          <p>
            Your substantial one-time contribution provides the resources needed for significant improvements
            and new initiatives. This level of support opens doors and creates opportunities that might not
            otherwise be possible. You're truly making history here!
          </p>
        </Fragment>
      )
    },
    'onetime-low': {
      title: 'Thank You! 🎉',
      primary: "Every contribution counts!",
      secondary: (
        <Fragment>
          You've contributed <strong className="SupportSuccessModal-amount">${(amount / 100).toFixed(2)}</strong>
          {' '}to <strong>{organization.name}</strong>
        </Fragment>
      ),
      impact: (
        <Fragment>
          <Icon type="heart" className="SupportSuccessModal-impact-icon" />
          <p>
            Your support, no matter the size, is valuable and appreciated! Together with others in the community,
            your contribution helps us continue our mission and grow stronger.
              Thank you for believing in what we're building!
          </p>
        </Fragment>
      )
    }
  }

  const messageTier = getMessageTier()
  const currentMessage = messages[messageTier]

  return (
    <Modal onClose={onClose} className="SupportSuccessModal">
      <div className="SupportSuccessModal-content">
        <div className="SupportSuccessModal-icon-wrapper">
          <div className="SupportSuccessModal-icon-circle">
            <Icon type="checkmark" className="SupportSuccessModal-icon" />
          </div>
          <div className="SupportSuccessModal-confetti">
            {[...Array(20)].map((_, i) => (
              <div key={i} className={`SupportSuccessModal-confetti-piece piece-${i}`} />
            ))}
          </div>
        </div>

        <h2 className="SupportSuccessModal-title">{currentMessage.title}</h2>

        <div className="SupportSuccessModal-message">
          <p className="SupportSuccessModal-text-primary">
            {currentMessage.primary}
          </p>
          <p className="SupportSuccessModal-text-secondary">
            {currentMessage.secondary}
          </p>
        </div>

        <div className="SupportSuccessModal-impact">
          {currentMessage.impact}
        </div>

        <div className="SupportSuccessModal-footer">
          <Button type="primary" onClick={onClose} className="SupportSuccessModal-button">
            Continue Exploring
          </Button>
        </div>
      </div>
    </Modal>
  )
}
